// DatePickerField — reusable MUI X DatePicker для React Hook Form.
// Локаль: ru (date-fns/locale). Формат: dd.MM.yyyy. minDate = today.
import type { ReactNode } from 'react';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';

interface DatePickerFieldProps<T extends FieldValues> {
  /** Имя поля в форме. */
  name: Path<T>;
  /** RHF control. */
  control: Control<T>;
  /** Label поля. */
  label: string;
  /** Обязательное поле. */
  required?: boolean;
  /** Helper text под полем. */
  helperText?: string;
  /** Минимальная допустимая дата (default = today). */
  minDate?: 'today' | Date;
  /** Максимальная допустимая дата. */
  maxDate?: Date;
  /** Disabled. */
  disabled?: boolean;
  /** Полная ширина. */
  fullWidth?: boolean;
  /** ARIA-label для a11y. */
  'aria-label'?: string;
}

/** Преобразует Date в ISO-строку yyyy-MM-dd (без времени). */
function toIsoDate(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Преобразует строку (yyyy-MM-dd или ISO Instant) в Date для DatePicker. */
function parseFieldValue(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const s = String(value);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Поле выбора даты (MUI X DatePicker) для React Hook Form. */
export function DatePickerField<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  helperText,
  minDate,
  maxDate,
  disabled = false,
  fullWidth = true,
  'aria-label': ariaLabel,
}: DatePickerFieldProps<T>): ReactNode {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const resolvedMinDate = minDate === 'today' ? new Date() : minDate;
        return (
        <DatePicker
          value={parseFieldValue(field.value)}
          onChange={(newValue: Date | null) => {
            field.onChange(toIsoDate(newValue));
          }}
          format="dd.MM.yyyy"
          {...(resolvedMinDate !== undefined && { minDate: resolvedMinDate })}
          {...(maxDate !== undefined && { maxDate })}
          disabled={disabled}
          slotProps={{
            textField: {
              label,
              required,
              fullWidth,
              error: Boolean(fieldState.error),
              helperText: fieldState.error?.message ?? helperText,
              inputProps: ariaLabel ? { 'aria-label': ariaLabel } : undefined,
            },
          }}
        />
        );
      }}
    />
  );
}